import { Reveal } from "@/components/Reveal";
import { PROFILE, IMG } from "@/data/profile";

export default function About() {
  return (
    <main className="pt-28 lg:pt-36">
      <section className="wrap">
        <Reveal>
          <p className="overline-mono">About</p>
          <h1 className="max-w-3xl font-serif text-4xl leading-[1.08] tracking-tight text-navy sm:text-5xl lg:text-6xl">
            A researcher's discipline, a consultant's bias for action.
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-12 grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="text-lg font-light leading-relaxed text-navy-soft sm:text-xl">{PROFILE.summary}</p>
              <p className="mt-6 leading-relaxed text-navy-soft">
                For ten years I have worked at the seam between analysis and decision — first inside a global
                IT consultancy's intelligence engine, then as a research specialist in deal advisory, now
                leading strategic advisory for urban, infrastructure and sustainability programmes.
              </p>
              <p className="mt-4 leading-relaxed text-navy-soft">
                What I do, in one line: convert complex research into C-suite-ready recommendations,
                diagnostic frameworks and implementation roadmaps — across sustainability, digital
                transformation and asset optimisation.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="overflow-hidden border border-line">
                  <img src={IMG.data} alt="" className="aspect-[4/5] w-full object-cover" />
                </div>
                <div className="absolute -bottom-4 -right-4 -z-10 h-full w-full border border-blue/40" aria-hidden="true" />
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="wrap py-24 lg:py-32">
        <Reveal>
          <p className="overline-mono">Career</p>
          <h2 className="font-serif text-3xl tracking-tight text-navy sm:text-4xl lg:text-5xl">Where the evidence was built.</h2>
        </Reveal>
        <div className="mt-14">
          {PROFILE.timeline.map((t, i) => (
            <Reveal key={t.company} delay={i * 0.05}>
              <div
                data-testid="career-timeline-item"
                className="grid gap-6 border-t border-line py-10 transition-colors duration-300 hover:bg-paper-band lg:grid-cols-12 lg:gap-10 lg:px-6"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue lg:col-span-3">{t.period}</p>
                <div className="lg:col-span-4">
                  <h3 className="font-serif text-2xl text-navy">{t.company}</h3>
                  <p className="mt-1 text-sm font-medium text-navy-mute">{t.role}</p>
                </div>
                <ul className="space-y-2 lg:col-span-5">
                  {t.points.map((p, j) => (
                    <li key={j} className="flex gap-3 text-sm leading-relaxed text-navy-soft">
                      <span className="mt-2.5 h-1 w-1 shrink-0 bg-blue" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="wrap pb-24 lg:pb-32">
        <Reveal>
          <p className="overline-mono">Expertise</p>
          <h2 className="font-serif text-3xl tracking-tight text-navy sm:text-4xl lg:text-5xl">Six clusters of practice.</h2>
        </Reveal>
        <div className="mt-14">
          {PROFILE.expertise.map((e, i) => (
            <Reveal key={e.title} delay={i * 0.03}>
              <div className="group grid grid-cols-[auto_1fr] items-baseline gap-6 border-t border-line py-6 transition-all duration-300 hover:border-blue lg:grid-cols-12 lg:px-6">
                <p className="text-[11px] font-semibold tracking-[0.2em] text-blue lg:col-span-2">0{i + 1}</p>
                <h3 className="font-serif text-xl text-navy transition-colors duration-300 group-hover:text-blue sm:text-2xl lg:col-span-5">
                  {e.title}
                </h3>
                <p className="col-span-2 text-sm leading-relaxed text-navy-soft lg:col-span-5">{e.blurb}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-paper-band py-24 lg:py-32">
        <div className="wrap grid gap-16 lg:grid-cols-2">
          <div>
            <Reveal>
              <p className="overline-mono">Recognition</p>
              <h2 className="font-serif text-3xl tracking-tight text-navy sm:text-4xl">Achievements & awards.</h2>
            </Reveal>
            <div className="mt-10 space-y-8">
              {PROFILE.achievements.map((a, i) => (
                <Reveal key={a.title} delay={i * 0.05}>
                  <div className="border-l-2 border-blue pl-6">
                    <h3 className="font-serif text-xl text-navy">{a.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-navy-soft">{a.detail}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal>
              <div className="mt-12">
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-navy-mute">Certifications</p>
                <ul className="mt-4 space-y-2">
                  {PROFILE.certifications.map((c) => (
                    <li key={c} className="text-sm text-navy-soft">
                      — {c}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
          <div>
            <Reveal>
              <p className="overline-mono">Education</p>
              <h2 className="font-serif text-3xl tracking-tight text-navy sm:text-4xl">The foundations.</h2>
            </Reveal>
            <div className="mt-10 space-y-6">
              {PROFILE.education.map((e) => (
                <Reveal key={e.degree}>
                  <div className="flex items-baseline justify-between gap-4 border-b border-line pb-4">
                    <p className="font-serif text-lg text-navy">{e.degree}</p>
                    <p className="text-xs font-semibold text-blue">{e.year}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal>
              <div className="mt-12">
                <p className="overline-mono">When I'm off the clock</p>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  {PROFILE.offTheClock.map((o) => (
                    <div key={o.title} className="border border-line bg-white p-6 transition-colors duration-300 hover:border-blue">
                      <h3 className="font-serif italic text-lg text-navy">{o.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-navy-soft">{o.detail}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}